
import React, { useState, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { insertChatProjectSchema, type ChatProject, type InsertChatProject } from '@shared/schema';
import {
  Folder,
  Plus,
  Edit2,
  Trash2,
  Users,
  MapPin,
  Building,
  GraduationCap,
  User,
  MoreHorizontal,
  Upload,
  FileText,
  X
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const projectTypeIcons = {
  CLIENT: Users,
  DESTINATION: MapPin,
  SUPPLIER: Building,
  TRAINING: GraduationCap,
  PERSONAL: User,
  GENERAL: Folder
};

const projectTypeLabels = {
  CLIENT: 'Client',
  DESTINATION: 'Destination',
  SUPPLIER: 'Supplier',
  TRAINING: 'Training',
  PERSONAL: 'Personal',
  GENERAL: 'General'
};

const DEFAULT_PROJECT_COLOR = '#6B7280';
const DEFAULT_FORM_COLOR = '#3B82F6';
const HEX_SIX_PATTERN = /^#[0-9A-F]{6}$/i;
const HEX_THREE_PATTERN = /^#[0-9A-F]{3}$/i;

const projectTypeKeys = Object.keys(projectTypeIcons) as Array<keyof typeof projectTypeIcons>;

type ProjectFileMetadata = {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  processed: boolean;
};

type MetadataRecord = Record<string, unknown>;

const isProjectTypeKey = (value: string | null | undefined): value is keyof typeof projectTypeIcons =>
  typeof value === 'string' && projectTypeKeys.includes(value as keyof typeof projectTypeIcons);

const normalizeProjectType = (value: string | null | undefined): keyof typeof projectTypeIcons =>
  isProjectTypeKey(value) ? value : 'GENERAL';

const expandShortHex = (value: string): string => `#${value.slice(1).split('').map((ch) => ch + ch).join('')}`;

const normalizeHexColor = (value: string | null | undefined, fallback = DEFAULT_PROJECT_COLOR): string => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const trimmed = value.trim();

  if (HEX_SIX_PATTERN.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  if (HEX_THREE_PATTERN.test(trimmed)) {
    return expandShortHex(trimmed).toUpperCase();
  }

  return fallback;
};

const withAlpha = (hex: string, alpha = '20'): string => `${hex}${alpha}`;

const isPlainObject = (value: unknown): value is MetadataRecord =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const isProjectFileMetadata = (value: unknown): value is ProjectFileMetadata => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.name === 'string' &&
    typeof candidate.size === 'number' &&
    typeof candidate.type === 'string' &&
    typeof candidate.uploadedAt === 'string' &&
    typeof candidate.processed === 'boolean'
  );
};

const parseProjectFiles = (value: unknown): ProjectFileMetadata[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isProjectFileMetadata).map((file) => ({
    ...file,
    uploadedAt: new Date(file.uploadedAt).toISOString(),
  }));
};

const toMetadataRecord = (value: unknown): MetadataRecord => (isPlainObject(value) ? value : {});

interface ChatProjectManagerProps {
  userId: number;
  onProjectSelect?: (project: ChatProject) => void;
  selectedProjectId?: number;
}

export default function ChatProjectManager({
  userId,
  onProjectSelect,
  selectedProjectId
}: ChatProjectManagerProps) {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ChatProject | null>(null);

  const { data: projects = [], isLoading, error } = useQuery<ChatProject[]>({
    queryKey: ['/api/chat-projects', userId],
    queryFn: async () => {
      return await apiRequest('GET', `/api/chat-projects?userId=${userId}`);
    },
  });

  const createProjectMutation = useMutation({
    mutationFn: async (projectData: InsertChatProject) => {
      return await apiRequest('POST', '/api/chat-projects', projectData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-projects', userId] });
      setIsCreateDialogOpen(false);
      toast({
        title: 'Success',
        description: 'Chat folder created successfully',
      });
    },
    onError: (mutationError: unknown) => {
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to create folder';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: async ({ id, projectData }: { id: number; projectData: Partial<InsertChatProject> }) => {
      return await apiRequest('PATCH', `/api/chat-projects/${id}`, projectData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-projects', userId] });
      setEditingProject(null);
      toast({
        title: 'Success',
        description: 'Folder updated successfully',
      });
    },
    onError: (mutationError: unknown) => {
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to update folder';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest('DELETE', `/api/chat-projects/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-projects', userId] });
      toast({
        title: 'Success',
        description: 'Folder deleted successfully',
      });
    },
    onError: (mutationError: unknown) => {
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to delete folder';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return <div className="p-4">Loading folders...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-600">Error loading folders</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Chat Folders</h3>
          <p className="text-sm text-muted-foreground">
            Organize your conversations into folders for better context and organization
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              New Folder
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Chat Folder</DialogTitle>
              <DialogDescription>
                Create a folder to organize related conversations with shared context
              </DialogDescription>
            </DialogHeader>
            <ProjectForm
              userId={userId}
              onSubmit={(data) => createProjectMutation.mutate(data)}
              isLoading={createProjectMutation.isPending}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3">
        {projects.map((project) => {
          const projectType = normalizeProjectType(project.projectType);
          const IconComponent = projectTypeIcons[projectType];
          const accentColor = normalizeHexColor(project.color, DEFAULT_PROJECT_COLOR);
          const backgroundColor = withAlpha(accentColor);
          const isSelected = selectedProjectId === project.id;

          return (
            <Card
              key={project.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
              }`}
              onClick={() => onProjectSelect?.(project)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="p-2 rounded-lg"
                      style={{ backgroundColor, color: accentColor }}
                    >
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">{project.name}</h4>
                      {project.description && (
                        <p className="text-sm text-muted-foreground">{project.description}</p>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {projectTypeLabels[projectType]}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(project.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={(event) => event.stopPropagation()}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditingProject(project);
                        }}
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(event) => {
                          event.stopPropagation();
                          if (confirm('Are you sure you want to delete this project?')) {
                            deleteProjectMutation.mutate(project.id);
                          }
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {projects.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="p-8 text-center">
              <Folder className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h4 className="font-medium mb-2">No folders yet</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first folder to organize your conversations
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Folder
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {editingProject && (
        <Dialog open={!!editingProject} onOpenChange={() => setEditingProject(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Folder</DialogTitle>
              <DialogDescription>
                Update folder details and settings
              </DialogDescription>
            </DialogHeader>
            <ProjectForm
              userId={userId}
              project={editingProject}
              onSubmit={(data) =>
                updateProjectMutation.mutate({
                  id: editingProject.id,
                  projectData: data,
                })
              }
              isLoading={updateProjectMutation.isPending}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ProjectForm({
  userId,
  project,
  onSubmit,
  isLoading,
}: {
  userId: number;
  project?: ChatProject;
  onSubmit: (data: InsertChatProject) => void;
  isLoading: boolean;
}) {
  const { toast } = useToast();
  const initialFiles = parseProjectFiles(project?.files);
  const [uploadedFiles, setUploadedFiles] = useState<ProjectFileMetadata[]>(initialFiles);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<InsertChatProject>({
    resolver: zodResolver(insertChatProjectSchema),
    defaultValues: {
      userId,
      projectId: project?.projectId ?? '',
      name: project?.name ?? '',
      description: project?.description ?? '',
      projectType: normalizeProjectType(project?.projectType),
      color: normalizeHexColor(project?.color, DEFAULT_FORM_COLOR),
      systemInstructions: project?.systemInstructions ?? '',
      files: initialFiles as unknown as InsertChatProject['files'],
      metadata: toMetadataRecord(project?.metadata) as InsertChatProject['metadata'],
    },
  });

  const uploadFilesToServer = async (files: File[]) => {
    if (!project?.projectId) {
      toast({
        title: 'Error',
        description: 'Please save the project before uploading files',
        variant: 'destructive',
      });
      return;
    }

    // Client-side file size validation (10MB limit)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ');
      toast({
        title: 'File Size Error',
        description: `The following files exceed the 10MB limit: ${fileNames}`,
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      // Upload with progress tracking
      const xhr = new XMLHttpRequest();

      const uploadPromise = new Promise<any>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('POST', `/api/chat-projects/${project.projectId}/upload`);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.withCredentials = true;
        xhr.send(formData);
      });

      const result = await uploadPromise;

      if (result.success && result.files) {
        setUploadedFiles((previous) => {
          const nextFiles = [...previous, ...result.files];
          form.setValue('files', (nextFiles as unknown) as InsertChatProject['files'], { shouldDirty: true });
          return nextFiles;
        });

        toast({
          title: 'Success',
          description: `${result.files.length} file(s) uploaded successfully`,
        });

        // Refresh project data
        queryClient.invalidateQueries({ queryKey: ['/api/chat-projects', userId] });
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload files',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    if (files.length === 0) {
      return;
    }

    await uploadFilesToServer(files);
    event.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await uploadFilesToServer(files);
    }
  };

  const removeFile = async (fileId: string) => {
    if (!project?.projectId) {
      toast({
        title: 'Error',
        description: 'Project not found',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Call backend API to delete file
      await apiRequest('DELETE', `/api/chat-projects/${project.projectId}/files/${fileId}`);

      // Update local state only after successful backend deletion
      setUploadedFiles((previous) => {
        const nextFiles = previous.filter((file) => file.id !== fileId);
        form.setValue('files', (nextFiles as unknown) as InsertChatProject['files'], { shouldDirty: true });
        return nextFiles;
      });

      toast({
        title: 'Success',
        description: 'File removed successfully',
      });

      // Refresh project data
      queryClient.invalidateQueries({ queryKey: ['/api/chat-projects', userId] });
    } catch (error) {
      console.error('File removal error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to remove file',
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = (values: InsertChatProject) => {
    const sanitizedColor = normalizeHexColor(typeof values.color === 'string' ? values.color : null, DEFAULT_FORM_COLOR);
    onSubmit({
      ...values,
      color: sanitizedColor,
      metadata: (values.metadata ?? {}) as InsertChatProject['metadata'],
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="instructions">Instructions</TabsTrigger>
            <TabsTrigger value="files">Files</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Smith Family Vacation"
                        {...field}
                        value={typeof field.value === 'string' ? field.value : ''}
                        onChange={(event) => field.onChange(event.target.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="projectId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="client-smith-vacation-2024"
                        {...field}
                        value={typeof field.value === 'string' ? field.value : ''}
                        onChange={(event) => field.onChange(event.target.value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Planning vacation for the Smith family to Europe"
                      {...field}
                      value={typeof field.value === 'string' ? field.value : ''}
                      onChange={(event) => field.onChange(event.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="projectType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={normalizeProjectType(field.value as string | null | undefined)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="CLIENT">Client</SelectItem>
                        <SelectItem value="DESTINATION">Destination</SelectItem>
                        <SelectItem value="SUPPLIER">Supplier</SelectItem>
                        <SelectItem value="TRAINING">Training</SelectItem>
                        <SelectItem value="PERSONAL">Personal</SelectItem>
                        <SelectItem value="GENERAL">General</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => {
                  const rawValue = typeof field.value === 'string' ? field.value : null;
                  const hexColor = normalizeHexColor(rawValue, DEFAULT_FORM_COLOR);
                  return (
                    <FormItem>
                      <FormLabel>Color</FormLabel>
                      <FormControl>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="color"
                            className="w-12 h-10 p-1 border rounded"
                            value={hexColor}
                            onChange={(event) => field.onChange(event.target.value)}
                          />
                          <Input
                            placeholder="#3B82F6"
                            value={hexColor}
                            onChange={(event) => field.onChange(event.target.value)}
                            className="flex-1"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          </TabsContent>

          <TabsContent value="instructions" className="space-y-4">
            <FormField
              control={form.control}
              name="systemInstructions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>System Instructions</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Customize how the AI behaves in this project. For example: 'You are a luxury travel specialist focused on Europe. Always suggest premium accommodations and experiences.'"
                      className="min-h-[200px]"
                      {...field}
                      value={typeof field.value === 'string' ? field.value : ''}
                      onChange={(event) => field.onChange(event.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-sm text-muted-foreground">
                    These instructions will be applied to all conversations within this project, giving the AI specific context and behavior guidelines.
                  </p>
                </FormItem>
              )}
            />
          </TabsContent>

          <TabsContent value="files" className="space-y-4">
            <div>
              <FormLabel>Project Files</FormLabel>
              <p className="text-sm text-muted-foreground mb-4">
                Upload documents, images, or other files to provide context for this project. The AI can reference these files during conversations.
              </p>

              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                } ${!project?.projectId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => project?.projectId && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.txt,.md,.csv,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                  disabled={!project?.projectId || isUploading}
                />
                <Upload className={`h-8 w-8 mx-auto mb-2 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className="text-sm font-medium mb-1">
                  {isDragging ? 'Drop files here' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-xs text-gray-500">PDF, DOC, TXT, MD, CSV, Excel, Images (max 10MB per file)</p>
                {!project?.projectId && (
                  <p className="text-xs text-amber-600 mt-2">
                    Save the project first before uploading files
                  </p>
                )}
                {isUploading && (
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600">Uploading... {uploadProgress}%</p>
                  </div>
                )}
              </div>

              {uploadedFiles.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2">Uploaded Files</h4>
                  <ScrollArea className="max-h-40">
                    <div className="space-y-2">
                      {uploadedFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-gray-500" />
                            <div>
                              <p className="text-sm font-medium">{file.name}</p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB • {new Date(file.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(file.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end space-x-2">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : project ? 'Update Project' : 'Create Project'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
